import { useToast } from "@chakra-ui/react";
import { useForm } from "react-hook-form";

import { COUNTRY_OPTIONS } from "@equidefi/constants/countries";
import { SmartFormProvider } from "@equidefi/portals/components/smart-form";
import { usePostalCodeExcluded } from "@equidefi/portals/hooks/usePostalCode";
import { useUpdateUserById } from "@equidefi/portals/hooks/useUsers";
import { Modal } from "@equidefi/ui";

interface EditContactInfoModalProps {
  modal: {
    isOpen: boolean;
    onClose: () => void;
  };
  user: User;
  onSuccess: () => void;
}

export const EditContactInfoModal = ({ modal, user, onSuccess }: EditContactInfoModalProps) => {
  const toast = useToast();
  const updateUser = useUpdateUserById(user?.id);

  const form = useForm({
    defaultValues: {
      phone: user?.phone,
      address_street_1: user?.address_street_1,
      address_street_2: user?.address_street_2,
      address_city: user?.address_city,
      address_state: user?.address_state,
      address_postal_code: user?.address_postal_code,
      address_country: user?.address_country,
    },
  });

  const hidePostalCode = usePostalCodeExcluded(form.watch("address_country"), form.watch("address_postal_code"), () =>
    form.setValue("address_postal_code", ""),
  );

  const onSubmit = async (data) => {
    try {
      await updateUser.mutateAsync(data);
      toast({
        status: "success",
        description: "Contact info updated successfully",
      });
      onSuccess();
      modal.onClose();
    } catch (error) {
      toast({
        status: "error",
        description: error.response?.data?.errors?.join(". ") || "Something went wrong",
      });
    }
  };

  return (
    <SmartFormProvider form={form}>
      {({ Input, PhoneField, Select }) => (
        <Modal
          title="Edit Contact Info"
          isOpen={modal.isOpen}
          onClose={modal.onClose}
          onSubmit={form.handleSubmit(onSubmit)}
          buttons={[
            { label: "Save", type: "submit", isLoading: updateUser.isLoading },
            { label: "Dismiss", action: modal.onClose, variant: "outline" },
          ]}>
          <PhoneField name="phone" label="Phone" />
          <Input name="address_street_1" label="Street Address" />
          <Input name="address_street_2" label="Street Address 2" />
          <Input name="address_city" label="City" />
          <Input name="address_state" label="State" />
          {!hidePostalCode && <Input name="address_postal_code" label="Postal Code" />}
          <Select name="address_country" label="Country" options={COUNTRY_OPTIONS} />
        </Modal>
      )}
    </SmartFormProvider>
  );
};
